import { isDate } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { formatDateForGql, parseHmisDateString } from '@/modules/hmis/hmisUtil';

// adapted from https://github.com/jschwindt/react-use-search-params-state/tree/main

type DefaultType =
  | string
  | number
  | boolean
  | Array<string | number | boolean>
  | Date
  | null;

type SearchParamStateType = {
  type: 'string' | 'number' | 'boolean' | 'date';
  default: DefaultType;
  multiple?: boolean;
};

export type SearchParamsStateType = Record<string, SearchParamStateType>;

const paramToBool = (
  paramName: string,
  paramDefinition: SearchParamStateType,
  searchParams: URLSearchParams
) => {
  const paramValue = searchParams.get(paramName);
  // The only presence of a boolean param (value === '') is considered true
  if (paramValue === 'true' || paramValue === '1' || paramValue === '')
    return true;
  if (paramValue === 'false' || paramValue === '0') return false;

  return paramDefinition.default;
};

const paramToDate = (
  paramName: string,
  paramDefinition: SearchParamStateType,
  searchParams: URLSearchParams
) => {
  const paramValue = searchParams.get(paramName);
  return parseHmisDateString(paramValue) || paramDefinition.default;
};

const paramToValue = (
  paramName: string,
  paramDefinition: SearchParamStateType,
  searchParams: URLSearchParams
) => {
  if (paramDefinition.multiple) {
    const paramValue = searchParams.getAll(paramName);
    if (paramValue.length > 0) {
      return paramDefinition.type === 'number'
        ? paramValue.map((value) => Number(value))
        : paramValue;
    }
  } else {
    const paramValue = searchParams.get(paramName);
    if (paramValue) {
      return paramDefinition.type === 'number'
        ? Number(paramValue)
        : paramValue;
    }
  }

  return paramDefinition.default;
};

const getValues = (
  paramsDefinition: SearchParamsStateType,
  searchParams: URLSearchParams
) => {
  const values: Record<string, any> = {};
  for (const [paramName, paramDefinition] of Object.entries(paramsDefinition)) {
    if (paramDefinition.type === 'boolean') {
      values[paramName] = paramToBool(paramName, paramDefinition, searchParams);
    } else if (paramDefinition.type === 'date') {
      values[paramName] = paramToDate(paramName, paramDefinition, searchParams);
    } else {
      values[paramName] = paramToValue(
        paramName,
        paramDefinition,
        searchParams
      );
    }
  }
  return values;
};

const getAllCurrentParams = (searchParams: URLSearchParams) => {
  const allUrlParams: Record<string, any> = {};
  searchParams.forEach((value, key) => {
    if (allUrlParams[key]) {
      if (Array.isArray(allUrlParams[key])) {
        allUrlParams[key].push(value);
      } else {
        allUrlParams[key] = [allUrlParams[key], value];
      }
    } else {
      allUrlParams[key] = value;
    }
  });
  return allUrlParams;
};

const populateParams = (
  params: Record<string, any>,
  searchParams: URLSearchParams
) => {
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v));
    } else {
      if (!!value) searchParams.set(key, value);
    }
  });
};

// Type for internal state representation of search params
type ValueType = Record<string, any>; // Future improvement: constrain value type

type Args = {
  paramsDefinition: SearchParamsStateType; // shape of params/state
  // Seeds missing URL params on mount. Existing URL params always take precedence.
  initial?: ValueType;
};

// Returns state and setState function (supports updater: setValues(prev => ({ ...prev, key: val })))
type SetStateAction = ValueType | ((prev: ValueType) => ValueType);
type ReturnType = [ValueType, (value: SetStateAction) => void];

const useSearchParamsState = ({
  paramsDefinition,
  initial,
}: Args): ReturnType => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  // `initial` is NOT passed to `useSearchParams` here, since react-router's
  // useSearchParams's `defaultInit` prop auto-populates the internal state, but not
  // the search params that appear in the url. See the useEffect below
  const [searchParams] = useSearchParams();

  const [mounted, setMounted] = useState(false);

  // Use an effect to set the `initial` params in the url bar. This could be avoided
  // if we transitioned to createBrowserRouter and used route `loader`s, but that's a big lift,
  // and would need to be configured on every route that uses optional columns.
  useEffect(() => {
    setMounted(true);

    if (!initial || Object.keys(initial).length === 0) return;

    const initialParamsToUpdate: Record<string, any> = Object.fromEntries(
      // Find any key/value pairs that are in initial, but not in searchParams
      Object.entries(initial).filter(([key]) => !searchParams.has(key))
    );
    const accumulator = new URLSearchParams();
    populateParams(initialParamsToUpdate, accumulator);
    if (accumulator.size === 0) return;

    // Add currentParams to avoid overwriting params set by other components
    const currentParams = getAllCurrentParams(searchParams);
    populateParams(currentParams, accumulator);

    navigate(
      {
        pathname,
        hash,
        search: accumulator.toString(),
      },
      { replace: true }
    ); // replace so that this doesn't create a new entry in browser history

    // Only run on mount, otherwise the effect prevents the user from overwriting initial values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const values = useMemo(() => {
    if (initial && !mounted) {
      // On first render, explicit URL params must win over `initial`. Otherwise callers
      // can observe the seeded values first and overwrite direct-link state such as
      // `?page=3&pageSize=10` before the URL-backed values ever render.
      const searchParamValues = getValues(paramsDefinition, searchParams);
      const valuesWithInitialFallback = { ...searchParamValues };
      Object.keys(paramsDefinition).forEach((key) => {
        if (!searchParams.has(key) && Object.hasOwn(initial, key)) {
          valuesWithInitialFallback[key] = initial[key];
        }
      });
      return valuesWithInitialFallback;
    }
    return getValues(paramsDefinition, searchParams);
  }, [initial, mounted, paramsDefinition, searchParams]);

  /**
   * Updates URL search params (same keys as paramsDefinition). Behaves like React setState:
   * - Pass an object to set/replace: keys present are updated, keys in paramsDefinition but
   * not in the object are cleared. setValues({}) clears all defined params.
   * - Pass an updater function to merge with current values: setValues(prev => ({ ...prev, page: 2 })).
   *
   * Any keys not present in paramsDefinition are ignored.
   *
   * @example
   * setValues({ page: 1, limit: 25 });           // set these, clear other defined params
   * setValues({});                               // clear all params in paramsDefinition
   * setValues(prev => ({ ...prev, page: prev.page + 1 }));  // merge: increment page only
   */
  const setValues = useCallback(
    (newValuesOrUpdater: SetStateAction) => {
      const currentParams = getAllCurrentParams(searchParams);
      const currentValues = getValues(paramsDefinition, searchParams);
      const nextValues =
        typeof newValuesOrUpdater === 'function'
          ? newValuesOrUpdater(currentValues)
          : newValuesOrUpdater;

      // Iterate paramsDefinition so setValues({}) clears all defined params
      for (const key of Object.keys(paramsDefinition)) {
        const hasKey = Object.prototype.hasOwnProperty.call(nextValues, key);
        let value = hasKey ? nextValues[key] : undefined;

        if (hasKey && paramsDefinition[key]?.type === 'date' && isDate(value)) {
          // Try to serialize date. If it is invalid, it will be set to null.
          value = formatDateForGql(value);
        }

        const isEmpty =
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);
        if (isEmpty) {
          delete currentParams[key];
        } else {
          currentParams[key] = value;
        }
      }

      const accumulator = new URLSearchParams();
      populateParams(currentParams, accumulator);
      // `navigate` instead of `useSearchParams` as workaround for https://github.com/remix-run/react-router/issues/8393
      navigate({
        pathname,
        hash,
        search: accumulator.toString(),
      });
    },
    [hash, navigate, paramsDefinition, pathname, searchParams]
  );
  return [values, setValues];
};
export default useSearchParamsState;
